import {
  type ComponentPropsWithoutRef,
  createContext,
  type ElementRef,
  type HTMLAttributes,
  type RefObject,
  useContext,
  useId,
} from 'react';

import { type Label as LabelPrimitive, Slot as SlotPrimitive } from 'radix-ui';
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  useFormContext,
  useFormState,
} from 'react-hook-form';

import { Label } from './label';
import { cn } from './lib/cn';

const Form = FormProvider;

interface FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
}

const FormFieldContext = createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => (
  <FormFieldContext.Provider value={{ name: props.name }}>
    <Controller {...props} />
  </FormFieldContext.Provider>
);

const useFormField = () => {
  const fieldContext = useContext(FormFieldContext);
  const itemContext = useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

interface FormItemContextValue {
  id: string;
}

const FormItemContext = createContext<FormItemContextValue>({} as FormItemContextValue);

const FormItem = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLDivElement> & { ref?: RefObject<HTMLDivElement | null> }) => {
  const id = useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div className={cn('flex flex-col gap-2', className)} ref={ref} {...props} />
    </FormItemContext.Provider>
  );
};
FormItem.displayName = 'FormItem';

interface FormLabelProps extends ComponentPropsWithoutRef<typeof LabelPrimitive.Root> {
  hideError?: boolean; // Hide error style for the label in case you want to show it in a different way e.g. sign in error message from clerk
}

const FormLabel = ({
  className,
  hideError,
  ref,
  ...props
}: FormLabelProps & { ref?: RefObject<ElementRef<typeof LabelPrimitive.Root> | null> }) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      className={cn(!hideError && error && 'text-red-dark', className)}
      htmlFor={formItemId}
      ref={ref}
      {...props}
    />
  );
};
FormLabel.displayName = 'FormLabel';

const FormControl = ({
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof SlotPrimitive.Slot> & {
  ref?: RefObject<ElementRef<typeof SlotPrimitive.Slot> | null>;
}) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return (
    <SlotPrimitive.Slot
      aria-describedby={error ? `${formDescriptionId} ${formMessageId}` : `${formDescriptionId}`}
      aria-invalid={!!error}
      id={formItemId}
      ref={ref}
      {...props}
    />
  );
};
FormControl.displayName = 'FormControl';

const FormDescription = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLParagraphElement> & { ref?: RefObject<HTMLParagraphElement | null> }) => {
  const { formDescriptionId } = useFormField();

  return <p className={cn('text-muted-foreground text-sm', className)} id={formDescriptionId} ref={ref} {...props} />;
};
FormDescription.displayName = 'FormDescription';

const FormMessage = ({
  className,
  children,
  ref,
  ...props
}: HTMLAttributes<HTMLParagraphElement> & { ref?: RefObject<HTMLParagraphElement | null> }) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message) : children;

  if (!body) {
    return null;
  }

  return (
    <p className={cn('font-normal text-destructive text-xs', className)} id={formMessageId} ref={ref} {...props}>
      {body}
    </p>
  );
};
FormMessage.displayName = 'FormMessage';

export { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, useFormField };
